<template>
  <GoogleAdPlaceholder
    v-if="showGAMAds"
    :ad-id="topPlacement[brand]"
    :page="pageType"
    :device="deviceType"
    :brand="brandName"
    :is-u-s-a-region="isUSA"
    :is-wedding-registry="isRegistry"
    :env-brand="brand"
  />
  <ClientOnly>
    <component :is="devLinks" />
  </ClientOnly>
  <BrandBanner />
  <Breadcrumb
    v-if="breadcrumbs.length"
    :model="breadcrumbs"
    :last-item-clickable="true"
    aria-label="breadcrumb"
    class="breadcrumbs"
    data-test-id="breadcrumbs"
  />

  <div class="grid-x margin-top-xs">
    <div class="top" :class="spacingClasses">
      <ProductCard />

      <ProsZoneAVertical v-if="isProsAVisible" class="prosAWrapper" :class="prosASpacingClass" />

      <div v-if="isPerksBelowZoneA && isMobile" class="cell margin-top-m">
        <GWPFacade v-if="isGwpPhase2" class="cell margin-bottom-m" data-testid="gwp-facade" />
        <LoyaltyPerksFacade class="cell margin-bottom-m" data-testid="loyalty-perks-section-treatment-2" />
        <ProductPerks />
      </div>
    </div>
  </div>
  <div v-if="!isNewUnavailablePage" :class="{ bottom: shoppableRedesignOptimization }">
    <div v-if="isMobile">
      <GoogleAdPlaceholder
        v-if="showGAMAds"
        :ad-id="inlinePlacement[brand]"
        :page="pageType"
        :brand="brandName"
        :is-u-s-a-region="isUSA"
        :is-wedding-registry="isRegistry"
        :custom-style-class="spacingClasses"
        :env-brand="brand"
      />

      <SponsoredItems
        v-if="showSponsoredItems"
        :site-mon-config="siteMonConfig"
        :is-monetization-enabled="isMonetizationEnabled"
        :enable-impression-tags-for-s-m-products="enableImpressionTagsForSMProducts"
        :enable-click-tags-for-s-m-products="enableClickTagsForSMProducts"
        :carousel-open-in-new-tab-enabled="carouselOpenInNewTabEnabled"
        :is-badge-sale-red="isBadgeSaleRed"
        :is-mcom="isMcom"
        :selected-upc="selectedUpcNumber"
      />
    </div>

    <div>
      <LazyLoadWhenInViewport :disabled="isShopTheCollectionExperimentEnabled" :root-margin="isPDPEnhancementUEnabled ? 1200 : 600">
        <ProsZoneBHorizontal v-if="prosZone && isPDPEnhancementWEnabled && !isStyliticsAbovePros && !isImageEntryEnabled2" />
      </LazyLoadWhenInViewport>

      <div id="outfitting-section-anchor"></div>

      <LazyLoadWhenInViewport :root-margin="1200">
        <Outfitting v-if="enableOutfittingWidget" v-bind="outfittingProps" :class="spacingClasses" />
      </LazyLoadWhenInViewport>

      <LazyLoadWhenInViewport :disabled="isShopTheCollectionExperimentEnabled" :root-margin="isPDPEnhancementUEnabled ? 1200 : 600">
        <ProsZoneBHorizontal v-if="prosZone && !isImageEntryEnabled2 && (!isPDPEnhancementWEnabled || isStyliticsAbovePros)" />
      </LazyLoadWhenInViewport>
    </div>

    <div v-if="!isMobile">
      <GoogleAdPlaceholder
        v-if="showGAMAds"
        :ad-id="inlinePlacement[brand]"
        :page="pageType"
        :device="deviceType"
        :brand="brandName"
        :is-u-s-a-region="isUSA"
        :is-wedding-registry="isRegistry"
        :custom-style-class="spacingClasses"
        :env-brand="brand"
      />

      <SponsoredItems
        v-if="showSponsoredItems"
        :site-mon-config="siteMonConfig"
        :is-monetization-enabled="isMonetizationEnabled"
        :enable-impression-tags-for-s-m-products="enableImpressionTagsForSMProducts"
        :enable-click-tags-for-s-m-products="enableClickTagsForSMProducts"
        :carousel-open-in-new-tab-enabled="carouselOpenInNewTabEnabled"
        :is-badge-sale-red="isBadgeSaleRed"
        :is-mcom="isMcom"
        :selected-upc="selectedUpcNumber"
      />
    </div>

    <div>
      <LazyLoadWhenInViewport>
        <Syndigo v-if="syndigo" :class="spacingClasses" />
      </LazyLoadWhenInViewport>
      <ReviewSection v-if="showReviewSection" :class="spacingClasses" />
    </div>

    <LazyLoadWhenInViewport v-if="isProsZoneCEnabled">
      <ProsZoneCHorizontal />
    </LazyLoadWhenInViewport>

    <LazyLoadWhenInViewport>
      <RecentlyViewedItems />
    </LazyLoadWhenInViewport>

    <GoogleAdPlaceholder
      v-if="showGAMAds && brand !== 'bcom' && deviceType !== 'mobile' && !isGiftWithPurchase"
      ad-id="googleAdOnPdpBottom"
      :page="pageType"
      :device="deviceType"
      :brand="brandName"
      :is-u-s-a-region="isUSA"
      :is-wedding-registry="isRegistry"
      :custom-style-class="spacingClasses"
      :env-brand="brand"
    />
  </div>
  <BrandBannerSecondary v-if="isChanelFlag" :class="spacingClasses" />
  <ProductQuickviewWrapper v-if="isProductQuickviewEnabled" />
</template>

<script setup>
import { defineAsyncComponent, ref, onMounted, computed, onBeforeUnmount } from 'vue';
import Breadcrumb from '@atomic-ui/breadcrumb/Breadcrumb';
import SponsoredItems from '@nuxt-common/site-monetization/app/components/SponsoredItems';
import GoogleAdPlaceholder from '@nuxt-common/site-monetization/app/components/GoogleAdPlaceholder';
import useUpc from '~/modules/upc/composables/useUpc';
import useOutfittingData from '~/composables/useOutfittingData';
import GWPFacade from '~/modules/gwp/components/GWPFacade.vue';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { brand } from 'config';
import { useBrand } from '~/composables/useBrand';
import { usePageData } from '~/composables/usePageData';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useSponsoredItems from '~/composables/useSponsoredItems';
import useDevLinks from '~/modules/dev/composables/useDevLinks';
import ProsZoneAVertical from '~/modules/pros/ProsZoneAVertical';
import ProductPerks from '~/modules/productPerks/components/ProductPerks';
import LoyaltyPerksFacade from '~/modules/productPerks/components/LoyaltyPerksFacade';
import useExperiments from '~/stores/useExperimentsStore';
import { useBreadcrumbs } from '~/modules/breadcrumbs/composables/useBreadcrumbs';
import BrandBannerSecondary from '~/components/brandBanner/BrandBannerSecondary.common';
import StaticImportReviewSection from '~/modules/reviews/components/ReviewSection';
import LazyHydratedReviewSection from '~/modules/lazy-hydration/components/ReviewSection';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { useProductQuickview } from '~/composables/product-quickview/useProductQuickview';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { preFetchAsyncComponents } from '~/utils/preFetchAsyncComponents';
import ProductCard from './ProductCard/ProductCard';
import BrandBanner from './brandBanner/BrandBanner.common';
import LazyLoadWhenInViewport from './common/LazyLoadWhenInViewport';

const { shoppingMode, regionCode } = usePageData();
const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();
const { brandName } = useCommonProductGetters();
const { isGiftWithPurchase } = useProductType();
const { isHermesFlag, isChanelFlag } = useProductFlags();
const { killswitches } = useKillswitches();
const selectedMemberId = useSelectedMemberId();
let ReviewSection = StaticImportReviewSection;

// TODO: remove later once lazy hydration testing is over
if (killswitches.isLazyHydrationEnabled) {
  ReviewSection = LazyHydratedReviewSection;
}

const {
  syndigo,
  prosZoneA,
  showSponsoredItems,
  showGAMAds,
  showReviewSection,
  shoppableRedesignOptimization,
  isStyliticsAbovePros,
  basePerksConditions,
  isProductPerksEnabled,
  isProductPerksEnabledPhase2,
  isNewUnavailablePage,
  isShopTheCollectionExperimentEnabled,
} = useFeatureEligibility();

const {
  siteMonConfig,
  isMonetizationEnabled,
  enableImpressionTagsForSMProducts,
  enableClickTagsForSMProducts,
  carouselOpenInNewTabEnabled,
  isBadgeSaleRed,
} = useSponsoredItems();

const {
  experimentFlags: {
    isImageEntryEnabled2,
    isSplitPerksEnabled,
    isSplitPerksV2Enabled,
    isSplitPerksV2EnabledSignedIn,
    isProsZoneCEnabled,
    isPRosZoneBEnabled,
    isGWPEnabled,
    isGWPEnabled2,
    isProsQuickShopEnabled,
  },
} = useExperiments();

const selectedPrice = useProductPrice();
const { breadcrumbs } = useBreadcrumbs();

const prosZone = computed(() => (isMcom && !isHermesFlag) || (isBcom && (isPRosZoneBEnabled || !isMobile.value)));

// TODO: imported files get loeaded twice. The issue should be fixed with SSR hydration fix on macys cli
const ProsZoneBHorizontal = defineAsyncComponent(() => import(/* webpackChunkName: 'ProsZoneB' */ '~/modules/pros/ProsZoneBHorizontal'));
const ProsZoneCHorizontal = defineAsyncComponent(() => import(/* webpackChunkName: 'ProsZoneC' */ '~/modules/pros/ProsZoneCHorizontal'));
const RecentlyViewedItems = defineAsyncComponent(() => import(/* webpackChunkName: 'RecentlyViewedItems' */ '~/modules/pros/RecentlyViewedItems'));
const Syndigo = defineAsyncComponent(() => import('~/modules/syndigo/components/SyndigoView'));
const topPlacement = { mcom: 'googleAdOnPdpTop', bcom: 'googleAdOnPdp' };
const inlinePlacement = { mcom: 'googleAdOnPdp', bcom: 'googleAdOnPdpBottom' };
const deviceType = isMobile.value ? 'mobile' : 'desktop';
const pageType = 'product';
const isUSA = regionCode === 'US';
const isRegistry = shoppingMode === 'WEDDING_REGISTRY';
const spacingClasses = shoppableRedesignOptimization
  ? 'small-margin-bottom-m large-margin-bottom-xl'
  : 'small-margin-bottom-xl large-margin-bottom-xxl';
const prosASpacingClass = shoppableRedesignOptimization ? 'margin-top-m' : 'margin-top-xl large-margin-top-z large-margin-left-xxl';

const isPerksBelowZoneA =
  basePerksConditions &&
  ((isProductPerksEnabled && isBcom && !isProductPerksEnabledPhase2) || // phase 1
    ((isSplitPerksV2Enabled || isSplitPerksV2EnabledSignedIn) && !isSplitPerksEnabled)); // phase 2

const { isPDPEnhancementWEnabled, isPDPEnhancementUEnabled } = killswitches;

// Pros
const isProsAVisible = computed(() => isMobile.value && prosZoneA);

const isGwpPhase2 = computed(() => !isGWPEnabled && isGWPEnabled2 && selectedPrice.value.isGWPAvailable);

// Outfitting & ProductQuickview Slideout
const { outfittingProps, enableOutfittingWidget } = useOutfittingData();
const { isPqvPayloadActive } = useProductQuickview();
const isGwpActive = computed(() => (isGWPEnabled || isGWPEnabled2) && selectedPrice.value?.isGWPAvailable);

// tells us whether the pqv is ready to be lazy-loaded or not
// If the outfitting widget is enabled and the pqvPayload is populated (outfitting product selected) -> lazy-load pqv
const isProductQuickviewEnabled = computed(
  () => !!(isPqvPayloadActive.value && (enableOutfittingWidget.value || isGwpActive.value || isProsQuickShopEnabled)),
);

const ProductQuickviewWrapper = defineAsyncComponent(
  () => import(/* webpackChunkName: 'ProductQuickviewWrapper' */ '~/components/slideoutContent/ProductQuickviewWrapper'),
);

const Outfitting = defineAsyncComponent(() => {
  preFetchAsyncComponents(ProductQuickviewWrapper);
  return import('@nuxt-common/outfitting/app/components/features/stylitics/Outfitting');
});

/** @type {import('vue').Ref<import('vue').DefineComponent<any, any, any>?>} */
const devLinks = ref(null);
const { selectedUpcNumber } = useUpc();

// const store = useStore(); // TODO

let timerId;

onMounted(() => {
  devLinks.value = useDevLinks()?.vueComponent;

  const recordViewedItem = async () => {
    try {
      const { addRecentlyViewedItems } = await import(/* webpackChunkName 'addRecentlyViewedItems' */ '@nuxt-common/pros-collection/app/utils/utils');
      if (!isGiftWithPurchase) addRecentlyViewedItems(selectedMemberId.value);
    } catch (e) {
      console.warn('Failed to import add recently viewed item', e);
    }
  };

  if (window?.requestIdleCallback) {
    timerId = window.requestIdleCallback(recordViewedItem, { timeout: 3000 });
  } else {
    timerId = setTimeout(recordViewedItem, 3000);
  }
});

onBeforeUnmount(() => {
  if (window?.cancelIdleCallback) window.cancelIdleCallback(timerId);
  else clearTimeout(timerId);
});
</script>

<style lang="scss">
@import '~settingSCSS';
@include cta-buttons;

@include breakpoint(medium down) {
  .breadcrumbs {
    .breadcrumb-dropdown-wrapper {
      ol.breadcrumb-dropdown {
        li {
          height: auto;
          min-height: rem-calc(18);
        }
      }
    }
  }
}

@include breakpoint(small only) {
  .outfitting #stylitics-container .stylitics-list-widget-base .stylitics-ui-heading {
    @include header(h4);
  }
}

@include breakpoint(small only) {
  .sponsored-items .carousel-container {
    .header-container .h3 {
      @include header(h4);
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~settingSCSS';
.top {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.bottom {
  :deep(.carousel-container) {
    .title-container {
      @include margin-bottom-s;
    }
  }
  // TODO Delete override in of h3 on this level
  // this adds custom spacing to h3 tags, so by changing to another tag like h5
  // this need to be overriden again to preserve the spacing
  // UMP-2696 is story in backlog for that
  // Places that might need adjustment, search for TD#1 in project

  :deep(.review-section) {
    h3 {
      @include breakpoint(large up) {
        @include margin-bottom-s;
      }
    }
  }
  :deep(.outfitting) {
    h3 {
      @include margin-bottom-s;
    }
  }
  :deep(.pros-grid-header) {
    @include padding-bottom-s;
  }
}
@include breakpoint(large) {
  .top {
    flex-direction: row;
  }
}
</style>
